import { Link } from 'react-router-dom'
import { ArrowRight, ArrowUpRight, MapPin } from 'lucide-react'
import PageHero from '../components/PageHero'
import SectionNav from '../components/SectionNav'
import { players, regions } from '../data/content'

const paths = [
  {
    title: 'Motriz',
    rule: 'Reglas 25.3 y 25.4',
    text: 'Amputación, lesión medular, parálisis. El swing se arma con prótesis, anclaje del palo o un monoplaza que entra al green.',
  },
  {
    title: 'Sensorial',
    rule: 'Regla 25.2',
    text: 'Ceguera, baja visión o sordera. En B1 el guía es parte del equipo. En 2025 México debutó en los Deaflympics.',
  },
  {
    title: 'Intelectual',
    rule: 'Regla 25.5',
    text: 'Con validación de Virtus se puede jugar con un ayudante además del caddie: reglas, estrategia y la bola en el green.',
  },
]

const Community = () => (
  <div className="min-h-screen bg-ivory">
    <PageHero
      eyebrow="Ocho estados · Cuatro países en 2026"
      title="La gente que"
      highlight="ya juega"
      description="De Xochimilco al ranking mundial. Estos son los nombres, las sedes y las formas de entrar."
    />

    <SectionNav
      items={[
        { href: '#jugadores', label: 'Jugadores' },
        { href: '#sedes', label: 'Sedes' },
        { href: '#caminos', label: 'Cómo se adapta' },
      ]}
    />

    <section className="border-b border-leaf/10 bg-cream">
      <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-y divide-leaf/10 sm:grid-cols-4 sm:divide-y-0">
        {[
          ['8', 'Estados con apoyo'],
          ['25', 'Jugadores en 2026'],
          ['4', 'Países en el Adaptive Open'],
          ['6', 'Referentes mexicanos'],
        ].map(([value, label]) => (
          <div key={label} className="px-4 py-8 text-center">
            <p className="font-serif text-3xl text-leaf md:text-4xl">{value}</p>
            <p className="mt-1 text-sm text-mist">{label}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="jugadores" className="scroll-mt-36 section-pad mx-auto max-w-7xl">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="eyebrow">Circuito</p>
          <h2 className="display mt-4 text-4xl md:text-5xl">Mexicanos que ya tienen resultado.</h2>
        </div>
        <Link to="/blog/mexicanos-en-el-circuito" className="text-sm font-semibold text-leaf hover:text-fairway">
          Leer las historias →
        </Link>
      </div>

      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {players.map((player) => (
          <article key={player.name} className="flex h-full flex-col rounded-3xl bg-white p-7 ring-1 ring-leaf/10">
            {player.photo && (
              <img
                src={player.photo}
                alt={player.photoAlt || player.name}
                className={`mb-5 aspect-[3/4] w-full rounded-2xl object-cover ${player.photoClass || 'object-top'}`}
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-sun px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ink">
                {player.rank}
              </span>
              <span className="text-sm text-mist">{player.category}</span>
            </div>
            <h3 className="mt-5 font-serif text-3xl leading-tight">{player.name}</h3>
            <p className="mt-2 flex items-center gap-1.5 text-sm text-leaf">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {player.place}
            </p>
            <p className="mt-4 leading-relaxed text-mist">{player.summary}</p>
            {player.highlights?.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-ink/80">
                {player.highlights.map((item) => (
                  <li key={item} className="border-l-2 border-leaf/30 pl-3">{item}</li>
                ))}
              </ul>
            )}
            {player.href && (
              <a
                href={player.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-leaf"
              >
                Perfil en EDGA
                <ArrowUpRight className="h-4 w-4" />
              </a>
            )}
          </article>
        ))}
      </div>
    </section>

    <section id="sedes" className="scroll-mt-36 bg-cream">
      <div className="section-pad mx-auto max-w-7xl">
        <div className="max-w-2xl">
          <p className="eyebrow">Dónde estamos</p>
          <h2 className="display mt-4 text-4xl md:text-5xl">La sede es Xochimilco. La red es el país.</h2>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {regions.map((region) => (
            <article
              key={region.name}
              className={`rounded-3xl p-6 ${
                region.type === 'Sede' ? 'bg-fairway text-cream sm:col-span-2 lg:col-span-2' : 'bg-white ring-1 ring-leaf/10'
              }`}
            >
              <p className={`text-xs font-semibold uppercase tracking-eyebrow ${region.type === 'Sede' ? 'text-sun' : 'text-leaf'}`}>
                {region.type}
                {region.state ? ` · ${region.state}` : ''}
              </p>
              <h3 className="mt-3 font-serif text-2xl">{region.name}</h3>
              <p className={`mt-3 leading-relaxed ${region.type === 'Sede' ? 'text-cream/80' : 'text-mist'}`}>
                {region.text}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="caminos" className="scroll-mt-36 section-pad mx-auto max-w-7xl">
      <div className="max-w-2xl">
        <p className="eyebrow">Tres entradas</p>
        <h2 className="display mt-4 text-4xl md:text-5xl">El deporte se adapta. La persona no tiene que hacerlo sola.</h2>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {paths.map((path, index) => (
          <article key={path.title} className="rounded-3xl bg-white p-7 ring-1 ring-leaf/10">
            <p className="font-serif text-4xl text-leaf">0{index + 1}</p>
            <h3 className="mt-4 font-serif text-2xl">{path.title}</h3>
            <p className="mt-2 text-sm font-semibold text-leaf">{path.rule}</p>
            <p className="mt-4 leading-relaxed text-mist">{path.text}</p>
          </article>
        ))}
      </div>
      <Link to="/golf#regla-25" className="btn-primary mt-10">
        Entender la Regla 25
        <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  </div>
)

export default Community
